import { useContext, useState, useEffect, FC, SyntheticEvent } from 'react';
import styles from './VerifyAccountScreen.module.scss';
import common from '../../util/scss/common.module.scss';
import { IconAlert } from '../../icons/Icons';
import PageLayout from '../../layout/PageLayout/PageLayout';
import VerifyAccountContext, {
    EVerifyAccountUiState,
    VerifyAccountProvider,
} from '../../contexts/VerifyAccountContext';
import CardLoader from '../../components/Loader/Loader';
import { Redirect } from 'react-router-dom';
import { EMixpanelEvent, ERoutes } from '../../util/constants';
import VerifyAccount from '../../components/VerifyAccount/VerifyAccount';
import Card from '../../components/Card/Card';
import GlobalContext from '../../contexts/GlobalContext';
import API from '../../api/api';
import CoBranding from '../../components/CoBranding/CoBranding';
import { LoadingButton } from '@mui/lab';
import { Alert, Snackbar } from '@mui/material';
import mixpanel from 'mixpanel-browser';

const VerifyAccountSuccess: FC = () => {
    const { businesses, legacySyncToken, hasFamilyUsers, primaryUser } = useContext(VerifyAccountContext);

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'verify_account' });
    }, []);

    return (
        <VerifyAccount
            businesses={businesses}
            primaryUser={primaryUser}
            hasFamilyUsers={hasFamilyUsers}
            legacySyncToken={legacySyncToken}
        />
    );
};

const VerifyAccountError: FC = () => {
    const { language, coBranding } = useContext(GlobalContext);
    const { sendVerificationEmail } = useContext(VerifyAccountContext);
    const [isLoading, setIsLoading] = useState(false);

    const handleSendEmail = async (event: SyntheticEvent) => {
        event.preventDefault();
        API.post.trackEvent({ event: 'Sign In || Verification Error | Button - Resend Verification Email' });
        mixpanel.track(EMixpanelEvent.interacted, { key: 'resend_verification' });
        setIsLoading(true);
        await sendVerificationEmail();
        setIsLoading(false);
    };

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Verification Error ' });
        mixpanel.track(EMixpanelEvent.actionFailed, { key: 'verify_account' });
    }, []);

    return (
        <>
            <Card>
                {(coBranding.logoUrl || coBranding.name) && (
                    <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
                )}
                <section className={`${common.icon}`} data-testid={'icon-error'}>
                    <IconAlert width={'65'} height={'65'} fillColor={'#696C74'} />
                </section>
                <header>
                    <h1 className={`${common.pageHeader} ${common.withSubhead}`}>{language.verify.error.header}</h1>
                    <h2 className={common.pageSubhead}>{language.verify.error.subhead}</h2>
                </header>
                <main className={styles.sendEmail}>
                    <section className={styles.cta}>
                        <LoadingButton
                            disabled={isLoading ? true : false}
                            loading={isLoading ? true : false}
                            onClick={handleSendEmail}
                            type="submit"
                            variant="contained"
                            size="large"
                        >
                            {language.verify.error.cta}
                        </LoadingButton>
                    </section>
                </main>
            </Card>
            <Snackbar>
                <Alert severity="success">{language.verify.sendSuccess}</Alert>
            </Snackbar>
        </>
    );
};

const VerifyAccountUi: FC = () => {
    const { language } = useContext(GlobalContext);
    const { verifyAccountUiState } = useContext(VerifyAccountContext);

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'verify_account_email' });
    }, []);

    let verifyAccountUi: React.ReactElement;
    switch (verifyAccountUiState) {
        case EVerifyAccountUiState.default:
        case EVerifyAccountUiState.initialized:
        case EVerifyAccountUiState.loginLinkParsed:
        case EVerifyAccountUiState.dataLoaded:
            verifyAccountUi = <CardLoader label={language.verify.loader} />;
            break;
        case EVerifyAccountUiState.ready:
            verifyAccountUi = <VerifyAccountSuccess />;
            break;
        case EVerifyAccountUiState.error:
            verifyAccountUi = <VerifyAccountError />;
            break;
        case EVerifyAccountUiState.fatalError:
            verifyAccountUi = <Redirect to={ERoutes.error} />;
            break;
        default:
            throw new Error('Verify Account UI State set incorrectly');
    }
    return verifyAccountUi;
};

const VerifyAccountScreen: FC = () => {
    return (
        <VerifyAccountProvider>
            <PageLayout>
                <VerifyAccountUi />
            </PageLayout>
        </VerifyAccountProvider>
    );
};

export default VerifyAccountScreen;
